import type { World } from '../sim/types';
import { plainTitle } from './surveyNames';
import { dateLabel, epochDayOf } from '../sim/calendar';
import { PROGRAMS } from '../sim/monitoring';
import { SPECIES, displayName } from '../sim/species';
import { knownOf, speciesHere, type Trend } from './overview';
import { escapeHtml } from './format';

/**
 * What the player sees change (M240, the brief's section 6 and 7): the monitoring's counts that came in lately, each
 * against the one before as the overview reads a trend (beyond both counts' errors, `surveyTrend`), and the
 * conservancy's journal, the log's dated entries of its work and the reef's events, newest first. Markup from the
 * world; nothing here reads the model's own numbers.
 */

/** A count that came in within a window, as Explore tells it. */
export interface RecentCount {
  id: string;
  name: string;
  day: number;
  text: string;
  trend: Trend;
}

/** The counts of the last `days` days (a scheduled program's or a commissioned survey's), newest first. */
export function recentCounts(world: World, days = 60): RecentCount[] {
  const today = epochDayOf(world);
  const names = new Map(SPECIES.map((s) => [s.id, displayName(s)]));
  // Only what lives at the sanctuary (M240's review: the right whale's count had opened Maui Nui's, "not at this sanctuary").
  const here = new Set(speciesHere(world).map((r) => r.id));
  return Object.entries(world.monitoring).flatMap(([id, list]) => {
    const last = list.at(-1);
    if (!last || today - last.day > days || !PROGRAMS[id] || !here.has(id)) return [];
    const known = knownOf(world, id);
    return [{ id, name: names.get(id) ?? id, day: last.day, text: known.text, trend: known.trend }];
  }).sort((a, b) => b.day - a.day || a.name.localeCompare(b.name));
}

const TREND_WORDS: Readonly<Record<Trend, string>> = { up: 'up since the count before', down: 'down since the count before', steady: 'no change past the counts\' error', unknown: 'the first count' };

/** The log's entries for the journal: the conservancy's work and the reef's events, not the months' accounts (Operations holds them). */
export function journalEntries(world: World, n = 8): { day: number; title: string; detail: string; severity: string }[] {
  return world.log.filter((e) => !/'s accounts$/.test(e.title)).slice(-n).reverse()
    .map((e) => ({ day: world.startEpochDay + e.day, title: e.title, detail: e.detail, severity: e.severity }));
}

/** Explore's sections of them: the recent counts, and the journal. */
export function changesHtml(world: World): string {
  const counts = recentCounts(world);
  const countList = counts.length > 0
    ? `<ul class="rows">${counts.slice(0, 8).map((c) => `<li><span class="row-name">${escapeHtml(c.name)}</span><span class="row-note">${escapeHtml(c.text)}; ${escapeHtml(TREND_WORDS[c.trend])}</span></li>`).join('')}</ul>`
    : '<p class="note">No count in the last two months. A survey brings one.</p>';
  const journal = journalEntries(world).map((e) => `<li class="journal-${escapeHtml(e.severity)}"><span class="row-name">${escapeHtml(plainTitle(e.title))}</span><span class="row-note">${escapeHtml(dateLabel(e.day))}. ${escapeHtml(e.detail)}</span></li>`).join('');
  return `<h3>Recent counts</h3>${countList}<h3>Journal</h3><ul class="rows journal">${journal}</ul>`;
}
